import React from "react";
import { Badge, Drawer } from "../../ui";
import { formatDate, formatMoney } from "../../data/format";
import { QuoteStageBadge, prospectStageInfo } from "../shared";
import ProposalDownloadSlot from "../clients/ProposalDownloadSlot";

/**
 * D6 referral proposal drawer (E's spec; Coordinator ruling B, retention-first).
 * Opened from a pipeline row's "N quotes" chip. Shows the case header, the
 * "Quotes so far", and a proposal-PDF download per quote when one exists
 * (download-only — staff upload on the client-hub Quotes tab).
 *
 * On a quote line E's grammar asks for carrier + split%; the current quote
 * model is thin (no carrier, no per-quote split %), so the line renders what
 * exists — status + target premium — and deliberately shows NO commission
 * dollar on an unbound quote (E's honesty law). Per-quote attribution + the
 * split % arrive with the post-gate prospect→quote linkage.
 */
function QuoteLine({ quote, caseTitle }) {
  const rawName =
    quote.quickName || [quote.firstName, quote.lastName].filter(Boolean).join(" ") || "";
  // E QA (D6 dev#1): when the quote name just echoes the drawer header, don't
  // repeat it — demote to "Quote · {target date}" and let premium carry the meta.
  const repeatsHeader =
    rawName && caseTitle && rawName.trim().toLowerCase() === caseTitle.trim().toLowerCase();
  const title = repeatsHeader
    ? `Quote${quote.targetDate ? ` · ${formatDate(quote.targetDate)}` : ""}`
    : rawName || "Quote";
  return (
    <div className="feat-caseq__line">
      <div className="feat-caseq__top">
        <span className="feat-caseq__name">{title}</span>
        <QuoteStageBadge quote={quote} />
      </div>
      <div className="feat-caseq__meta">
        {quote.targetPremium ? `${formatMoney(quote.targetPremium)} target` : "Premium pending"}
        {!repeatsHeader && quote.targetDate ? ` · ${formatDate(quote.targetDate)}` : ""}
      </div>
      <ProposalDownloadSlot quoteId={quote._id} />
    </div>
  );
}

function CaseQuotesDrawer({ prospect, quotes, onClose }) {
  const info = prospect ? prospectStageInfo(prospect.pipelineStage) : null;
  const list = quotes || [];
  return (
    <Drawer open={Boolean(prospect)} onClose={onClose} title={prospect ? prospect.name : "Quotes"}>
      {prospect ? (
        <>
          <div className="feat-caseq__header">
            {info ? <Badge variant={info.variant}>{info.label}</Badge> : null}
            {prospect.renewalDate ? (
              <span className="feat-cell-secondary">renews {formatDate(prospect.renewalDate)}</span>
            ) : null}
          </div>
          <h3 className="feat-kv-section__title">Quotes so far</h3>
          {list.length === 0 ? (
            <p className="feat-cell-secondary">No quotes on this case yet.</p>
          ) : (
            <div className="feat-caseq__lines">
              {list.map((q) => (
                <QuoteLine key={q._id} quote={q} caseTitle={prospect.name} />
              ))}
            </div>
          )}
        </>
      ) : null}
    </Drawer>
  );
}

export default CaseQuotesDrawer;
